import { useEffect, useRef } from 'react';
import { dismissToasts } from '@pypestream/design-system';

import { tableStore, ValidationMode } from '../stores/table-store';
import { sendUserMessage } from '../xstate/app.xstate-utils';
import { useProStudioCtxSelector } from '../xstate/app.xstate';
import { useCurrentRoute } from './use-current-route';
import { ProStudioPages } from './use-pro-studio-routes';

const VALIDATION_TOAST_ID = 'toast-automatic-validation';

const showManualModeToast = () =>
  sendUserMessage({
    toastId: VALIDATION_TOAST_ID,
    type: 'success',
    text: 'Automatic validation is OFF',
    autoClose: false,
  });

/**
 * Manages the "Automatic validation is OFF" toast across navigation and
 * solution switches:
 *
 * - Navigating AWAY from Workflows → dismisses the toast
 * - Navigating TO Workflows with manual mode active → shows the toast
 * - Switching to a different solution → saves the outgoing mode, restores the
 *   incoming one (Auto by default). If the restored mode is Manual and the
 *   user is already on Workflows, the toast is shown immediately; if Auto,
 *   any stale toast from the previous solution is dismissed.
 */
export const useValidationModeToast = () => {
  const botId = useProStudioCtxSelector((ctx) =>
    ctx.customerId && ctx.botName
      ? `${ctx.customerId}.${ctx.botName}`
      : undefined
  );

  const { label } = useCurrentRoute();
  const prevLabelRef = useRef<string | null>(label);
  const prevBotIdRef = useRef<string | undefined>(botId);

  // Show / dismiss toast on navigation between pages
  useEffect(() => {
    const prevLabel = prevLabelRef.current;
    const isNowWorkflows = label === ProStudioPages.WORKFLOWS;
    const wasWorkflows = prevLabel === ProStudioPages.WORKFLOWS;

    if (wasWorkflows && !isNowWorkflows) {
      dismissToasts(VALIDATION_TOAST_ID);
    } else if (
      !wasWorkflows &&
      isNowWorkflows &&
      tableStore.validationMode === ValidationMode.Manual
    ) {
      showManualModeToast();
    }

    prevLabelRef.current = label;
  }, [label]);

  // Save / restore validation mode per solution and sync the toast accordingly.
  // `label` is included in deps so the effect always reads the current page
  // when the bot changes (satisfies exhaustive-deps; the early-return guard
  // ensures no side-effects fire when only the label changes).
  useEffect(() => {
    const prevBotId = prevBotIdRef.current;

    if (botId === prevBotId) return;

    const restoredMode = tableStore.switchBotValidationMode(prevBotId, botId);

    if (
      restoredMode === ValidationMode.Manual &&
      label === ProStudioPages.WORKFLOWS
    ) {
      showManualModeToast();
    } else if (restoredMode === ValidationMode.Auto) {
      dismissToasts(VALIDATION_TOAST_ID);
    }

    prevBotIdRef.current = botId;
  }, [botId, label]);
};
